import React, { FC } from 'react'
import { Item } from '@radix-ui/react-dropdown-menu'
import { Box, Flex, Text } from '@moonpig/launchpad-components'
import { RadioButton } from '@moonpig/launchpad-forms'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useLocaleText as useReminderLocaleText } from '@moonpig/web-shared-reminders'
import { Relationship } from '@moonpig/web-explore-types-graphql'
import { groupRecipientsAlphabetically } from './buildRecipientOptions'

const StyledDropdownMenuItem = styled(Item)`
  margin: 1px;
  cursor: pointer;

  &:hover {
    outline: none;
    ${s({ bgcolor: 'colorBackground02' })}
  }
`

const RecipientRow = styled.label`
  display: flex;
  cursor: pointer;
  ${s({
    alignItems: 'center',
    pl: 4,
    pr: 6,
    py: 3,
    width: '100%',
  })}

  & > *:first-child {
    margin-right: 12px;
  }
`

const LetterHeading = styled(Text)`
  ${s({
    pl: 6,
    pt: 4,
    display: 'block',
    fontWeight: 700,
    color: 'colorTextLabel',
  })}
`

type RecipientListProps = {
  groupedRecipients: ReturnType<typeof groupRecipientsAlphabetically>
  selectedRecipientId: string | null
  onSelect: (id: string) => void
  inDropdownMenu: boolean
}

export const RecipientList: FC<RecipientListProps> = ({
  groupedRecipients,
  selectedRecipientId,
  onSelect,
  inDropdownMenu,
}) => {
  const localiseReminderText = useReminderLocaleText()

  return (
    <Box data-testid="recipients-filter-list">
      {groupedRecipients.map(group => (
        <Box key={group.letter}>
          <LetterHeading as="div">{group.letter}</LetterHeading>
          {group.recipients.map(recipient => {
            const content = (
              <RecipientRow htmlFor={recipient.id}>
                <RadioButton
                  id={recipient.id}
                  name="recipients-filter"
                  checked={selectedRecipientId === recipient.id}
                  onChange={() => onSelect(recipient.id)}
                  value={recipient.id}
                />
                <Flex flexDirection="column" alignItems="flex-start">
                  <Text
                    fontWeight={
                      selectedRecipientId === recipient.id ? 500 : 300
                    }
                    color="colorTextLabel"
                  >
                    {recipient.name}
                  </Text>
                  <Text color="colorBlack40">
                    {localiseReminderText('common.reminder.relationship', {
                      relationship: recipient.relationship as Relationship,
                    })}
                  </Text>
                </Flex>
              </RecipientRow>
            )

            if (!inDropdownMenu) {
              return <Box key={recipient.id}>{content}</Box>
            }

            return (
              <StyledDropdownMenuItem
                key={recipient.id}
                textValue={recipient.name}
                role="menuitemradio"
                aria-checked={selectedRecipientId === recipient.id}
                onSelect={event => {
                  event.preventDefault()
                  onSelect(recipient.id)
                }}
              >
                {content}
              </StyledDropdownMenuItem>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}
